import React, { useState } from 'react';
import { ArrowRight, Mail, Globe, User, MessageSquare, Type, Copy, Check, Music2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { EMAILS_MARQUEE } from '../data/mock';

const InputField = ({ label, icon: Icon, value, onChange, placeholder, type = 'text' }) => (
  <div>
    <label className="block text-[11px] tracking-[0.3em] text-white/50 mb-2 uppercase">{label}</label>
    <div className="relative">
      {Icon && (
        <Icon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" strokeWidth={1.6} />
      )}
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-white/[0.03] border border-white/10 rounded-xl pl-11 pr-4 py-3.5 text-white text-[14px] placeholder-white/30 outline-none focus:border-trillex-orange/60 focus:bg-white/[0.05] transition-colors"
      />
    </div>
  </div>
);

const Contact = () => {
  const [tab, setTab] = useState('general');
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '', link: '', artist: '', website: '' });

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const copyEmail = (mail) => {
    navigator.clipboard?.writeText(mail);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
    toast({ title: 'Email copied', description: mail });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Missing info', description: 'Name, email and message are required.' });
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch('/api/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: tab,
          name: form.name,
          email: form.email,
          subject: form.subject,
          message: form.message,
          artist: form.artist,
          link: form.link,
          website: form.website, // honeypot
        }),
      });
      const data = await res.json().catch(() => ({ ok: false, error: 'Bad response' }));

      if (res.ok && data.ok) {
        toast({
          title: tab === 'general' ? 'Inquiry sent' : 'Demo submitted',
          description: "We'll get back within 48 hours.",
        });
        setForm({ name: '', email: '', subject: '', message: '', link: '', artist: '', website: '' });
      } else {
        toast({
          title: 'Could not send',
          description: data.error || 'Please try again in a moment.',
        });
      }
    } catch (err) {
      toast({ title: 'Network error', description: 'Please check your connection and try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative pt-28 pb-20 overflow-hidden">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="flex items-center gap-3 mb-10">
          <span className="text-[11px] tracking-[0.35em] text-trillex-orange">— CONTACT / SUBMISSIONS</span>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-14">
          <div className="relative inline-flex p-1 rounded-full bg-white/[0.04] border border-white/10">
            <span
              className={`absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-full bg-gradient-to-r from-trillex-orange to-trillex-orange-light transition-all duration-500 ease-out ${
                tab === 'general' ? 'left-1' : 'left-[calc(50%+3px)]'
              }`}
            />
            <button
              onClick={() => setTab('general')}
              className={`relative z-10 px-7 py-2.5 text-[13px] tracking-wide font-semibold rounded-full transition-colors duration-300 ${
                tab === 'general' ? 'text-white' : 'text-white/70 hover:text-white'
              }`}
            >
              General Inquiry
            </button>
            <button
              onClick={() => setTab('demo')}
              className={`relative z-10 px-7 py-2.5 text-[13px] tracking-wide font-semibold rounded-full transition-colors duration-300 ${
                tab === 'demo' ? 'text-white' : 'text-white/70 hover:text-white'
              }`}
            >
              Demo Submission
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left */}
          <div>
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[11px] tracking-[0.3em] text-white/70 mb-8">
              CONTACT US
            </div>
            <h2 className="text-white font-black tracking-tight leading-[1.02] text-[clamp(44px,6.2vw,96px)]">
              GET IN{' '}
              <span className="bg-gradient-to-r from-trillex-orange via-trillex-orange-light to-[#ffc1c9] bg-clip-text text-transparent">
                TOUCH.
              </span>
            </h2>
            <p className="mt-6 text-white/55 text-[15px]">Need a faster response? Contact us directly here.</p>
            <button
              onClick={() => copyEmail('contact@kiuamusicgroup.com')}
              className="mt-2 inline-flex items-center gap-2 text-trillex-orange underline decoration-trillex-orange/40 underline-offset-4 hover:decoration-trillex-orange text-[16px]"
            >
              contact@kiuamusicgroup.com
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>

            <div className="mt-10 max-w-sm">
              <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                <div className="w-11 h-11 rounded-full bg-trillex-orange/15 flex items-center justify-center">
                  <Globe size={18} className="text-trillex-orange" strokeWidth={1.8} />
                </div>
                <div>
                  <div className="text-white text-[15px] font-semibold">Main Office</div>
                  <div className="text-white/55 text-sm">Turkey, Izmir</div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={submit}
            className="rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-white/[0.01] p-6 md:p-10 backdrop-blur-sm space-y-5"
          >
            <InputField label="Full Name" icon={User} value={form.name} onChange={onChange('name')} placeholder="Enter your full name..." />
            <InputField label="Email Address" icon={Mail} type="email" value={form.email} onChange={onChange('email')} placeholder="Enter your email address..." />
            {tab === 'demo' && (
              <>
                <InputField label="Artist / Project Name" icon={Music2} value={form.artist} onChange={onChange('artist')} placeholder="Your artist name..." />
                <InputField label="Demo Link (Soundcloud / Dropbox)" icon={Type} value={form.link} onChange={onChange('link')} placeholder="https://..." />
              </>
            )}
            <InputField label="Subject" icon={MessageSquare} value={form.subject} onChange={onChange('subject')} placeholder="Enter subject..." />
            <div>
              <label className="block text-[11px] tracking-[0.3em] text-white/50 mb-2 uppercase">Message</label>
              <div className="relative">
                <textarea
                  value={form.message}
                  onChange={onChange('message')}
                  maxLength={500}
                  rows={5}
                  placeholder="Enter your main text here..."
                  className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-white text-[14px] placeholder-white/30 outline-none focus:border-trillex-orange/60 focus:bg-white/[0.05] transition-colors resize-none"
                />
                <span className="absolute bottom-3 right-3 text-[11px] text-white/40">{form.message.length}/500</span>
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-trillex-orange to-trillex-orange-light text-white font-bold py-3.5 text-[14px] tracking-wide hover:brightness-110 transition-all shadow-[0_10px_40px_-10px_rgba(255,32,64,0.6)] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? 'Sending...' : (<>Submit Form <ArrowRight size={16} /></>)}
            </button>
            {/* Honeypot — hidden from real users */}
            <input
              type="text"
              tabIndex="-1"
              autoComplete="off"
              value={form.website}
              onChange={onChange('website')}
              style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
              aria-hidden="true"
            />
          </form>
        </div>
      </div>

      {/* Email marquee */}
      <div className="mt-24 select-none">
        <div className="trillex-marquee whitespace-nowrap flex gap-12 text-[clamp(48px,7vw,120px)] font-black tracking-tight text-white/8">
          {[...EMAILS_MARQUEE, ...EMAILS_MARQUEE, ...EMAILS_MARQUEE].map((m, i) => (
            <button
              key={i}
              onClick={() => copyEmail(m)}
              className="inline-flex items-center gap-4 hover:text-trillex-orange transition-colors"
            >
              {m}
              <ArrowRight size={48} className="opacity-50" strokeWidth={1.4} />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Contact;
